import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCtaPress } from './shared';
import type { TemplateProps } from './types';

export function ShieldTemplate({ state, onAccept }: TemplateProps) {
  const { t } = useTranslation();
  const { onClick } = useCtaPress(onAccept);
  const showButton = state.status === 'awaiting-click';
  const showSpinner =
    state.status === 'loading' || state.status === 'verifying' || state.status === 'redirecting';
  const isError = state.status === 'error';

  const statusText: Record<string, string> = {
    loading: t('shield.status.loading'),
    verifying: t('shield.status.verifying'),
    'awaiting-click': t('shield.status.awaitingClick'),
    redirecting: t('shield.status.redirecting'),
    error: t('shield.status.error'),
  };

  return (
    <main className="relative mx-auto flex min-h-dvh w-full max-w-xl flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 backdrop-blur">
        <ShieldCheck className="h-7 w-7 text-emerald-400" />
      </div>
      <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
        {t('shield.title')}
      </h1>
      <p className="mt-3 max-w-md text-balance text-sm text-zinc-400 sm:text-base">
        {t('shield.subtitle')}
      </p>
      <div className="mt-10 flex min-h-[3rem] items-center justify-center">
        {showButton ? (
          <button
            type="button"
            onClick={onClick}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-zinc-900 shadow-lg transition-transform active:scale-95 hover:-translate-y-0.5 hover:shadow-xl"
          >
            {t('shield.continue')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        ) : (
          <div
            className={`flex items-center gap-3 rounded-full border px-4 py-2 text-sm ${isError ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-white/10 bg-white/5 text-zinc-200'}`}
          >
            {showSpinner && <Loader2 className="h-4 w-4 animate-spin text-zinc-300" />}
            <span>{statusText[state.status]}</span>
          </div>
        )}
      </div>
      <p className="mt-6 text-xs text-zinc-500">{t('shield.footnote')}</p>
    </main>
  );
}
